/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import {
    arrayOf, func, number, object, shape, string, bool,
} from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import {
    NativeSelect,
    FormControl,
} from '@material-ui/core';
import Select from '@material-ui/core/Select';
import InputLabel from '@material-ui/core/InputLabel';
// helper
import subscriptionConfig from '../../../../helpers/common/subscription/subscription-helpers';

const styles = (theme) => ({
    frequencyWrapper: {
        width: '99%',
        display: 'flex',
        marginRight: '1%',
        [theme.breakpoints.down('375')]: {
            marginRight: '0',
            justifyContent: 'space-between',
        },
    },
    frequencySelectRoot: {
        border: '1px solid #dcdcdc',
        boxSizing: 'border-box',
        paddingLeft: '10px',
        minHeight: '32px',
        position: 'relative',
        fontSize: '14px',
        width: '100%',
        minWidth: 'fit-content',
        textAlign: 'right',
        borderRadius: '8px',
        '&:focus': {
            borderRadius: '8px',
            borderColor: '#dcdcdc',
        },
    },
    frequencySelect: {
        width: 175,
        minWidth: 'fit-content',
        lineHeight: '20px',
        [theme.breakpoints.down(1121)]: {
            fontSize: 13,
        },
        [theme.breakpoints.down(540)]: {
            width: 164,
        },
        '& .MuiSelect-icon': {
            color: theme.palette?.cms?.svgVactorColor || '#C4C4C4',
        },
    },
    locationTypeLabel: {
        display: 'flex',
        alignItems: 'center',
        fontWeight: 'bold',
        marginRight: 20,
        width: 80,
        lineHeight: '20px',
        fontSize: 14,
        color: theme.palette.cms?.black || '#000',
        [theme.breakpoints.down(1121)]: {
            marginRight: 15,
            width: 70,
        },
    },
    subscriptionContent: {
        marginBottom: 12,
    },

    select: {
        width: '100%',
        minHeight: '56px',
        border: 'solid 1px #C4C4C4',
        borderRadius: 3,
        padding: '2px 12px 2px 0px',
        margin: '0px !important',
        background: theme.palette.tiers?.card?.backgroundColor,
        '& :focus': {
            background: '#ffffff',
        },
        '& .MuiNativeSelect-select': {
            color: theme.palette.giftFinderSelectColor,
            fontSize: '14px',
            lineHeight: '26px',
            padding: '17px 12px 0px',
            minHeight: '26px',
        },
        '& .MuiNativeSelect-filled': {
            border: 'solid 1px #C4C4C4',
            borderRadius: 3,
        },
        '& svg': {
            fill: '#000000',
            top: 'calc(50% - 6px)',
        },
    },
    label: {
        top: '13px',
        left: '-2px',
        color: '#555555',
    },
    subscriptionWrapper: {
        margin: '0px',
        padding: '4px 18px 4px 0px',
        '& $frequencyWrapper': {
            width: '100%',
            marginRight: '0px',
            '& >.MuiFormControl-root': {
                width: '100%',
            },
        },
        [theme.breakpoints.up('600')]: {
            width: 'calc(100% - 51%)',
            padding: '4px 0px',
        },
    },
});

const SubscriptionDropDown = ({
    classes,
    subscriptionsList,
    handleChange,
    selectValue,
    dropdownType,
    frequencyOrDuration,
    fieldLabel,
    isPdpRedesignEnabled,
}) => {
    const dropdownListArray = Object.keys(subscriptionConfig?.[dropdownType]).map((item) => {
        const obj = {
            id: Number(item), // convert string to integer
            value: subscriptionConfig?.[dropdownType][item],
        };
        return obj;
    });

    const subscriptionOptionList = dropdownListArray.filter((item) => subscriptionsList?.[frequencyOrDuration]?.includes?.(item.id));
    return (
        <div className={`${classes.subscriptionContent} ${isPdpRedesignEnabled ? classes.subscriptionWrapper : ''}`}>
            <div className={classes.frequencyWrapper}>
                {isPdpRedesignEnabled ? (
                    <FormControl role="region" aria-label={`${frequencyOrDuration}_select`} variant="outlined">
                        <InputLabel
                            className={`${classes.locationTypeLabel} ${classes.label}`}
                            htmlFor={`${frequencyOrDuration  }_name`}
                        >
                            {fieldLabel}
                        </InputLabel>
                        <NativeSelect
                            defaultValue=""
                            className={`${classes.select}`}
                            value={selectValue}
                            data-testid="pdp-frequency-type"
                            disableUnderline
                            onChange={(e) => handleChange(e, frequencyOrDuration, subscriptionOptionList)}
                            inputProps={{
                                name: { frequencyOrDuration },
                                id: `${frequencyOrDuration}_name`,
                                'aria-hidden': 'true',
                                tabIndex: '-1',
                            }}
                        >
                            {subscriptionOptionList.map((item) => <option value={item?.id} aria-hidden="true" key={item.id}>{item?.value}</option>)}
                        </NativeSelect>
                    </FormControl>
                ) : (
                    <>
                        <InputLabel
                            className={classes.locationTypeLabel}
                            htmlFor={`${frequencyOrDuration}_name`}
                        >
                            {fieldLabel}
                        </InputLabel>
                        <Select
                            className={classes.frequencySelect}
                            name={frequencyOrDuration}
                            id={`${frequencyOrDuration}_name`}
                            title={frequencyOrDuration}
                            data-testid="pdp-frequency-type"
                            native
                            disableUnderline
                            value={selectValue}
                            inputProps={{
                                classes: {
                                    select: classes.frequencySelect,
                                    root: classes.frequencySelectRoot,
                                    icon: classes.frequencySelectIcon,
                                },
                            }}
                            onChange={(e) => handleChange(e, frequencyOrDuration, subscriptionOptionList)}
                        >
                            {subscriptionOptionList.map((item) => <option value={item?.id} key={item.id}>{item?.value}</option>)}
                        </Select>
                    </>
                )}
            </div>
        </div>
    );
};

SubscriptionDropDown.propTypes = {
    classes: object.isRequired,
    subscriptionsList: shape({
        duration: arrayOf(
            number,
        ),
        type: arrayOf(
            string,
        ),
        interval: arrayOf(
            number,
        ),
    }).isRequired,
    handleChange: func.isRequired,
    frequencyOrDuration: string,
    selectValue: string.isRequired,
    dropdownType: string,
    fieldLabel: string,
    isPdpRedesignEnabled: bool,
};

SubscriptionDropDown.defaultProps = {
    frequencyOrDuration: 'interval',
    fieldLabel: 'Frequency:',
    dropdownType: 'subscription_interval',
    isPdpRedesignEnabled: false,
};
export default (withStyles(styles)(SubscriptionDropDown));
